export type AdditionalUserInfoDto = {
  height?: number;
  weight?: number;
  gender?: string;
  surgery_date?: string;
  surgical_leg?: string;
  medical_condition?: string;
  ethnicity?: string;
  is_test_user?: boolean;
  date_of_birth?: string;
  prefix?: string;
  // Clinician/admin professional details. Optional, entered at create time
  // and editable later. `credentials` is one of 'PT' | 'PTA'.
  credentials?: string;
  license_number?: string;
  // Clinician/admin contact phone, from Settings > My Profile. Optional, free text.
  phone?: string;
  icd10_desc?: string;
  icd10_codes?: string;
  assessment?: string;
  primary_complaint?: string;
  current_level_of_function?: string;
  diagnosis?: string;
  // Clinical info for ALL of the patient's cases (a case can override it), plus
  // `<key>_summary` / `<key>_summary_source_hash` - see clinical-info/clinicalInfo.ts.
  short_term_goals?: string;
  long_term_goals?: string;
  initial_evaluation?: string;
  [clinicalInfoKey: string]: unknown;
};

const USER_ROLL = ['patient', 'clinician', 'admin'] as const;
export type UserRole = (typeof USER_ROLL)[number];

export function isUserRole(str: string): str is UserRole {
  return USER_ROLL.includes(str as UserRole);
}

export type UserInfoDto = {
  email: string;
  role: UserRole;
  name: string;
  middle_name?: string;
  last_name: string;
  additional_user_info?: AdditionalUserInfoDto;
  emr_user_id?: string;
  // Defaults to true on the server. Treated as true when undefined to keep legacy
  // payloads (which never carried this field) behaving as before.
  active?: boolean;
};

export type UpdateUserFormDto = {
  role: string;
  name: string;
  last_name: string;
  additional_user_info?: AdditionalUserInfoDto;
};

export type UserDto = {
  user_id: string;
  user_info: UserInfoDto;
  user_created_date: string;
  last_visit_date?: string;
};

// Open-ended bag of per-case fields stored in Case.AdditionalInfo
// (jsonb on the backend). Each key is optional; clients write whatever
// they have. The portal uses this to carry:
//   * name              — user-visible case name. Set by the clinician
//                         when adding a case manually, or pulled from
//                         WebPT's CaseTitle on EMR import.
//   * body_parts        — legacy field (still written by older flows).
//   * icd10_codes       — array of ICD-10 code strings (e.g. ["M25.562"]).
//                         Imported from WebPT, or typed by the clinician on a
//                         non-EMR case via the per-case ICD-10 table.
//   * icd10_descriptions — parallel array of human descriptions for those
//                         codes (e.g. ["Pain in left knee"]), imported from
//                         WebPT's Diagnosis[].description. Index-aligned with
//                         icd10_codes when present; absent for hand-typed
//                         codes. Feeds the note-dictation prompt's diagnosis
//                         block, which reads better with real text than a
//                         bare code.
//   * cause             — RelatedCause label from WebPT (e.g. "Sports Injury").
//   * therapist_name    — assigned therapist's full name.
//   * physician_name    — referring physician (House, Aaron MD).
//   * emr_case_id       — when this Percix Case mirrors a WebPT case,
//                         the numeric WebPT CaseID. Lets us round-trip
//                         to the EMR's case-specific URL on writes.
export type CaseAdditionalInfo = {
  name?: string;
  body_parts?: string[];
  icd10_codes?: string[];
  icd10_descriptions?: string[];
  cause?: string;
  therapist_name?: string;
  physician_name?: string;
  emr_case_id?: string;
  // Clinical info overriding the patient's All-cases value for THIS case, plus
  // `<key>_summary` / `<key>_summary_source_hash` - see clinical-info/clinicalInfo.ts.
  short_term_goals?: string;
  long_term_goals?: string;
  initial_evaluation?: string;
  [clinicalInfoKey: string]: unknown;
};

export type CaseDto = {
  case_id: string;
  active: boolean;
  // True for the (at most one) case the Patient App should resolve "the
  // assigned plan" against. Defaults to false; legacy patients without an
  // active case keep their overall-latest plan behavior.
  is_active_plan_case?: boolean;
  created_at: string;
  updated_at: string;
  additional_info?: CaseAdditionalInfo;
};

// The Clinical information tab of one case (GET/PUT /case-clinical-info).
// `fields` maps "<section>.<group>.<field>" to its text; only non-empty fields
// are stored. `version` is 0 until the first save.
export type CaseClinicalInfoDto = {
  case_id: string;
  fields: Record<string, string>;
  version: number;
  updated_at?: string;
  updated_by?: string;
};

type CommentDto = {
  assignor_user_id: string;
  date: string;
  comment: string;
};

export type TreatmentPlanDto = {
  treatment_plan_id: string;
  case_id: string;
  name: string;
  description: string;
  assigned_date: string;
  assignor_user_id: string;
  // Display name of the assigning clinician; absent from older servers.
  assignor_name?: string | null;
  start_date: string;
  end_date: string;
  comments: CommentDto[];
  movements: MovementDto[];
  // Bumped once per in-place edit of the plan, by a clinician's save or the patient moving an
  // exercise to the end. Absent from older servers.
  version?: number;
};

export type TreatmentPlanInputDto = {
  name: string;
  description: string;
  assignor_user_id: string;
  start_date: string;
  end_date?: string;
  comments: CommentDto[];
  movements: (MovementInputDto & {
    // The exercise's POSITION, 1..N in list order.
    plan_index: number;
    // PATCH only. Which existing slot this entry is, so the server does not have to infer
    // it from the position. Absent on an exercise the clinician just added — that is how
    // an addition is expressed — and absent entirely on the POST path.
    slot_id?: string;
    // PATCH only. The exercise the client believes this slot CURRENTLY holds, used as
    // optimistic concurrency: a disagreement means another clinician changed the plan
    // since it was loaded, and the save is refused rather than overwriting their edit.
    expected_movement_id?: string;
  })[];
  state: '';
  case_id: string;
  // PATCH only. The clinician's reason for changing the plan mid-treatment;
  // stored on the revision and shown in the Session view.
  update_reason?: string;
  // PATCH only. The plan version this edit was made against. The server refuses the save with
  // 409 `plan_version_changed` when the plan has moved on since, rather than writing a stale
  // order over a newer one.
  expected_version?: number;
};

export type TreatmentPlanTemplateDto = {
  treatment_plan_id: string;
  name: string;
  description: string;
  movements: MovementDto[];
};

export type TreatmentPlanTemplateInputDto = {
  name: string;
  description: string;
  movements: (MovementInputDto & { plan_index: number })[];
  state: '';
};

const TIME_UNITS = ['minutes', 'seconds', 'hours'] as const;

const UNIT_TYPE = [
  ...TIME_UNITS,
  'degrees',
  'millimeters',
  'centimeter',
  'meters',
  'kilogram',
  'none',
] as const;

type UnitType = (typeof UNIT_TYPE)[number];

export type ResultsDto = {
  key: string;
  value: number;
  unit: UnitType;
};

export type MovementResultsDto = {
  treatment_plan_id: string;
  movement_id: string;
  movement_details?: {
    movement_id: string;
    movement_info?: MovementBaseDto &
      MovementInputData & { settings: MovementInfoSetting } & { supported?: string };
  };
  session_id: string;
  sub_session_id: string;
  stop_date: string;
  start_date: string;
  // The position this run was recorded at — NOT a way to identify the exercise, since a
  // later reorder moves it. Match on slot_id.
  plan_index: number;
  // The slot this run belongs to. Absent on runs recorded before slot identity existed.
  slot_id?: string;
  status: string;
  status_description: string;
  // Set when this run was abandoned by a restart rather than completed. Deliberately NOT the
  // Replaced status: the run stays fully visible in history — the clinician should see that an
  // exercise was restarted and why — while being excluded from slot resolution server-side.
  // Absent on every run predating the restart feature, so treat undefined as "not restarted".
  restarted_reason?: 'PatientRequested' | 'PlanUpdated';
  // Free text the PATIENT typed when confirming their own restart. Only ever present alongside
  // restarted_reason === 'PatientRequested'; a plan-update restart carries the CLINICIAN's reason
  // instead, which lives on the plan revision, not here.
  restarted_note?: string;
  movement_completion_rate: number;
  results_data?: {
    sets_info?: {
      set: number;
      reps: number;
      hold: number;
      duration: number;
      is_partial?: boolean;
    }[];
    summary?: {
      phase_eng: number[];
      num_of_sets?: number;
      post_metrics_array?: {
        metric_name: string;
        metric_data: {
          side: string;
          value?: number;
          unit: string;
          metric_sd?: number | 'NA';
          metric_mcs?: number;
        };
        metric_stats?: {
          std?: number;
          mean?: number;
          median?: number;
          max_value_frame?: number;
        };
      }[];
    };
    rpe_survey?: {
      rpe?: string;
      pain?: string;
    };
    transcription_comment?: string;
    /** Where the algo ran: 'local' when the patient app ran it on the tablet; absent = cloud. */
    algo_mode?: string;
  };
};

export type MovementResultsOutputDto = {
  movements: MovementResultsDto[];
};

export type MovementMediaDto = {
  mediaLink: string;
  expiresInSec: number;
};

type ErrorResponseBody = {
  title: string;
  details: string;
};

export function isErrorResponseBody(data: unknown): data is ErrorResponseBody {
  // `data` is an axios response body, which can be a string (some APIs return a
  // raw-string 409 like "User already exists"), null, or an object. The `in`
  // operator throws a TypeError on non-object operands, so narrow to a non-null
  // object BEFORE probing for keys — otherwise a string body crashes the
  // httpClient error interceptor instead of falling through to normal handling.
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  return 'title' in data && 'details' in data;
}

export type RealtimeMetric = {
  metric_name: string;
  unit: string;
  max_range?: number;
  min_range?: number;
};

export type PostMetricDto = {
  metric_name: string;
  display_name: string;
  unit: string;
  improvement_direction: ImprovementDirections;
  graph_type: 'time' | 'scatter' | 'NA';
  'Metric math': 'min' | 'max' | 'average' | 'NA';
  min?: number;
  max?: number;
  metric_sd: number | 'NA';
  metric_mcs: number;
  metric_eng_phase: number;
};

export type MovementBaseDto = {
  exercise_name: string;
  position?: string;
  plane?: string;
  description?: string;
  body_parts?: string[];
  post_exercise_metric?: PostMetricDto[];
  realtime_metric?: RealtimeMetric[];
  //Old required equipment array
  assistant_device_needed?: string[];
  // New required equipment array
  assistant_equipment_needed?: Equipment[];
  // New optional equipment array
  assistant_equipment_optional?: Equipment[];
  'eng._phase'?: number;
  rep_on?: string[];
  category: string[];
  end_condition: EndCondition;
  time_per_meter?: string;
  non_algo_pem?: PostMetricDto[];
};

export type EndCondition =
  | 'reps'
  | 'reps_non_algo'
  | 'time'
  | 'total_distance'
  | 'position'
  | 'guided_rep_non_algo';

export const DONT_CARE = 'Dont Care';
export type OptionsSetting = {
  options: string[];
};

export type NumericOptionsSetting = {
  options: string[];
};

export type OptionsExpressionSetting = {
  options_expression: string;
};

export type NumericSetting = {
  min?: number;
  max?: number;
  step_size?: number;
  default?: number;
};

export type MovementDefinitionDto = {
  movement_id: string;
  movement_info: MovementBaseDto & { settings: MovementInfoSetting };
};

type MovementInputData = {
  type?: string[];
  side?: string;
  load?: { unit: string; value: string };
  sets?: number;
  reps?: number;
  minimumToConsiderRep?: number;
  hold?: number;
  // Seconds per rep. The only fractional dosage field — every other one is an integer.
  rate?: number;
  rest?: number;
  distance?: number;
  duration?: number;
  // Old optional equipment array
  assistant_device_optional?: string[];
  // New all-inclusive equipment array
  equipment?: Equipment[];
  goals?: { metric_name: string; value: number }[];
  comments?: string;
  require_patient_acknowledgment?: boolean;
  init_angle_degrees?: number;
};

export type MovementInputDto = {
  movement_id: string;
  movement_info: MovementInputData;
};

export type MovementDto = {
  movement_id: string;
  movement_info: MovementBaseDto & MovementInputData;
  // The slot's POSITION in the plan, 1..N. Order only — it is rewritten on every save,
  // so it never identifies an exercise. Use slot_id for that.
  plan_index?: number;
  // The slot's PERMANENT identity. This is what ties a recorded result to the exercise it
  // was performed against, and what lets an exercise be moved, removed or inserted without
  // re-attributing anyone's work. Absent from a backend that predates it, which is exactly
  // what the save path feature-detects on.
  slot_id?: string;
  // The exercise this slot PRESCRIBES. Differs from movement_id whenever a result
  // row wins the resolved render (a completed run, a continuation) — movement_id
  // then reports what was performed. Echo THIS back as expected_movement_id on the
  // in-place PATCH, which validates against the raw prescription row.
  // Absent from older backends; callers fall back to movement_id.
  prescribed_movement_id?: string;
};

export type MovementInfoSetting = {
  type?: OptionsSetting;
  side?: OptionsSetting;
  load?: OptionsSetting;
  sets?: NumericSetting;
  reps?: NumericSetting;
  minimum_to_consider_rep?: NumericOptionsSetting;
  initial_angle?: NumericOptionsSetting;
  hold?: NumericSetting;
  rest?: NumericSetting;
  distance?: NumericSetting;
  duration?: NumericSetting;
  assistant_device_optional?: OptionsExpressionSetting;
  // Seconds per rep. The library DOES send a step_size of 0.25 here — it is declared so the
  // shape is honest, because a type that omits a field the wire carries does not remove it.
  // Leaving it off was tried and did nothing: types are erased, validateSetting read step_size
  // at runtime regardless, and the form rejected its own seeded default (2.86, 5.41, 2.09 — 202
  // of the 292 rate defaults are not multiples of 0.25). The step check is therefore turned off
  // explicitly, in validateActivityRate. Rate is bounds-checked only.
  rate?: {
    default: number;
    max: number;
    min: number;
    unit: string;
    step_size?: number;
  };
};

export type Equipment = {
  Units?: string;
  Default?: string;
  Min?: number;
  Max?: number;
  Name: string;
  Group: number;
  Options?: string[];
};

export type MovementExecutionMetricResults = {
  metric_name: string;
  metric_data: {
    side: string;
    value: number[];
  };
};

export type Expression = { metric: string; side: string } | { operator: string };

export type QualitativeOptional = {
  expression: Expression[];
  violation_time: number;
  upper_range: number;
  lower_range: number;
  message_id: number;
};

export type DaaOptionalData = {
  index: number;
  message_id: number;
  violation: string;
};

export type MovementExecutionMetricsDTO = {
  overtime_metrics: MovementExecutionMetricResults[];
  qualitative_overtime_metrics?: (number[] | null)[];
  metadata?: {
    video_delta_frames: number;
    qualitative_optional_list?: QualitativeOptional[];
    daa_optional_list?: DaaOptionalData[];
  };
  version?: number;
};

export type TranscriptSessionDto = {
  transcript_media_upload: {
    mediaLink: string;
    expiresInSec: number;
  };
  transcript_session_status: {
    session_id: string;
    transcript_status: string;
  };
};

export type TranscriptOutputDto = {
  // transcript_session_output?: {
  //   status: string;
  //   results: {
  //     transcripts: { transcript: string }[];
  //   };
  // };
  transcript_session_output?: string;
  transcript_session_status: {
    session_id: string;
    transcript_status: string;
  };
};

export type SummaryProcessingDTO = {
  session_id: string;
  summary_status: 'Processing';
};

export type SummaryOutputDTO = {
  summary_session_status: {
    session_id: string;
    // 'Streaming' is NON-TERMINAL: partial output has arrived and more is
    // coming. Only PLAN_CHAT produces it; the one-shot assist and
    // session-insights flows go Processing -> Completed | Error.
    summary_status: 'Processing' | 'Streaming' | 'Completed' | 'Error';
  };
  summary_session_output: string;
};

export type PatientPrivacySetting = {
  agree: boolean;
  consent_date: string | undefined;
  document_id: string;
  document_version: string | undefined;
};

export enum ImprovementDirections {
  Up = 'up',
  Down = 'down',
  Goal = 'goal',
}

export type ClinicalNoteDto = {
  id: string;
  patient_id: string;
  case_id?: string;
  // The case the note's session data was pulled from (page-header case at
  // New-Note time). Informational; case_id is the case the note is filed
  // under. Absent for manually-typed notes and pre-existing notes.
  session_case_id?: string;
  author_user_id: string;
  note_type: string;
  note_text: string;
  created_at: string;
  updated_at?: string;
  metadata?: Record<string, any>;
  // Set together by the Save-and-Submit-to-EMR flow on success.
  last_emr_submitted_at?: string;
  last_emr_adapter_id?: string;
  last_emr_case_id?: string;
};

export type SystemVersionDto = {
  version: string;
  components?: {
    [component_name: string]: {
      version: string;
    };
  };
};

// EMR integration DTOs. Mirror the backend shapes from
// treatment-plan-handler/DTO/Emr*/*.cs. Step 3 of the EMR integration plan.

export type EmrAdapterDto = {
  id: string;
  adapter_id: string;
  display_name: string;
  host_patterns?: string[];
  page_recognizers?: unknown[];
  actions?: unknown[];
  min_extension_version?: string;
};

export type EmrLinkStatus = 'NotConnected' | 'Connected' | 'Mismatch' | 'NeedsReview';

export type EmrLinkDto = {
  id: string;
  patient_user_id: string;
  emr_adapter_id: string;
  emr_patient_id: string;
  status: EmrLinkStatus;
  last_verified_at?: string;
  last_verified_by_user_id?: string;
  last_import_summary?: Record<string, unknown>;
  created_at: string;
  updated_at?: string;
};

export type EmrAuditLogDto = {
  id: string;
  timestamp_utc: string;
  clinic_id?: string;
  actor_user_id: string;
  percix_patient_id: string;
  emr_adapter_id: string;
  emr_patient_id?: string;
  action_type: string;
  input_summary?: Record<string, unknown>;
  result_status: string;
  error_summary?: string;
  browser_extension_version?: string;
  correlation_id: string;
  step_trace?: unknown[];
};

// GET /treatment-plan/revisions — one in-place edit to an assigned plan.
//
// A SUMMARY, not the audit row: the server deliberately withholds the before/after prescription
// blobs (see PlanRevisionDto on the C# side), so this carries only what the Session view renders.
export type PlanRevisionDto = {
  plan_id: string;
  version: number;
  changed_by_user_id?: string;
  changed_at: string;
  // Optional by design — a clinician confirming a live-session edit must not be blocked by a text
  // box — so render the "Reason:" clause only when it is actually present.
  reason?: string;
  // The streaming session in flight when the edit landed. Null/absent for an ordinary
  // out-of-session edit, which must not surface on any session.
  session_id?: string;
  changed_plan_indices: number[];
};
